/**
 * Environment utilities for consistent environment detection across upCaret apps.
 * Handles case-insensitive comparison of VITE_ENV, MODE, and NODE_ENV.
 * 
 * This module is for browser/Vite app code. For vite.config.ts, use the Node.js module:
 * import { isProduction } from '@upcaret/env-utils/node.mjs';
 */

import type { Environment } from './types';
import { ENV, normalizeEnv } from './types';

export type UpcaretPublicEnv = Partial<{
  VITE_ENV: string;
  VITE_APP_URI: string;
  VITE_EXTRACTION_API_URL: string;
  VITE_TUS_UPLOAD_URL: string;
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_TURNSTILE_SITE_KEY: string;
  VITE_PUBLIC_POSTHOG_KEY: string;
  VITE_CENTLY_PUBLIC_POSTHOG_KEY: string;
  VITE_UPCARET_PUBLIC_POSTHOG_KEY: string;
}>;

declare global {
  // Runtime-injected public env values (e.g. from Cloudflare Worker or SSR).
  // This allows TanStack Start/SSR deployments to provide env at request-time
  // instead of relying on build-time `import.meta.env` substitution.
  var __UPCARET_PUBLIC_ENV__: UpcaretPublicEnv | undefined;
}

// Re-export types and constants for convenience
export type { Environment } from './types';
export { ENV, normalizeEnv } from './types';

export function getPublicEnv(): UpcaretPublicEnv {
  return (globalThis.__UPCARET_PUBLIC_ENV__ ?? {}) as UpcaretPublicEnv;
}

/**
 * Browser-only: static switch so Vite can replace each case at build time.
 * Dynamic access like `import.meta.env[key]` is forbidden in Vite's SSR module
 * runner, so we use a switch with literal string cases instead.
 */
function readViteInlinedEnv(key: keyof UpcaretPublicEnv): string | undefined {
  // Only run in a browser context — SSR relies on __UPCARET_PUBLIC_ENV__ alone.
  if (typeof window === 'undefined') return undefined;

  // Each case uses a string literal so Vite can statically replace the value.
  switch (key) {
    case 'VITE_ENV':               return (import.meta.env as UpcaretPublicEnv).VITE_ENV;
    case 'VITE_APP_URI':           return (import.meta.env as UpcaretPublicEnv).VITE_APP_URI;
    case 'VITE_EXTRACTION_API_URL': return (import.meta.env as UpcaretPublicEnv).VITE_EXTRACTION_API_URL;
    case 'VITE_TUS_UPLOAD_URL':    return (import.meta.env as UpcaretPublicEnv).VITE_TUS_UPLOAD_URL;
    case 'VITE_SUPABASE_URL':      return (import.meta.env as UpcaretPublicEnv).VITE_SUPABASE_URL;
    case 'VITE_SUPABASE_ANON_KEY': return (import.meta.env as UpcaretPublicEnv).VITE_SUPABASE_ANON_KEY;
    case 'VITE_TURNSTILE_SITE_KEY': return (import.meta.env as UpcaretPublicEnv).VITE_TURNSTILE_SITE_KEY;
    case 'VITE_PUBLIC_POSTHOG_KEY': return (import.meta.env as UpcaretPublicEnv).VITE_PUBLIC_POSTHOG_KEY;
    case 'VITE_CENTLY_PUBLIC_POSTHOG_KEY': return (import.meta.env as UpcaretPublicEnv).VITE_CENTLY_PUBLIC_POSTHOG_KEY;
    case 'VITE_UPCARET_PUBLIC_POSTHOG_KEY': return (import.meta.env as UpcaretPublicEnv).VITE_UPCARET_PUBLIC_POSTHOG_KEY;
    default: return undefined;
  }
}

/**
 * Get a public env value.
 * Priority: runtime-injected (__UPCARET_PUBLIC_ENV__) → Vite-inlined (import.meta.env).
 *
 * In production / SSR, the Cloudflare Worker / Vite config sets __UPCARET_PUBLIC_ENV__
 * before any module runs. The import.meta.env fallback handles browser-only dev
 * scenarios where the injection script hasn't executed yet (e.g. TanStack Start
 * hydration ordering).
 */
export function getPublicEnvVar(
  key: keyof UpcaretPublicEnv,
): string | undefined {
  const injected = globalThis.__UPCARET_PUBLIC_ENV__?.[key];
  if (typeof injected === 'string' && injected.length > 0) return injected;

  const inlined = readViteInlinedEnv(key);
  if (typeof inlined === 'string' && inlined.length > 0) return inlined;

  return undefined;
}

/**
 * Get a required public env value. Throws when missing.
 */
export function requirePublicEnvVar(key: keyof UpcaretPublicEnv): string {
  const value = getPublicEnvVar(key);
  if (!value) {
    throw new Error(`[env-utils] Missing required public env: ${String(key)}`);
  }
  return value;
}

/**
 * Get the current environment from runtime-injected public env.
 * Returns a normalized lowercase environment string.
 * Falls back to 'development' when VITE_ENV is not set (local dev without full env).
 */
export function getEnv(): Environment {
  // Prefer runtime-injected env when available (TanStack Start / SSR / Workers).
  // This is the most reliable source in deployments where client bundles may not
  // inline `VITE_ENV` consistently.
  const injectedEnv = getPublicEnvVar('VITE_ENV');
  return normalizeEnv(String(injectedEnv ?? 'development'));
}

/**
 * Check if the current environment is production.
 */
export function isProduction(): boolean {
  return getEnv() === ENV.PRODUCTION;
}

/**
 * Check if the current environment is staging.
 */
export function isStaging(): boolean {
  return getEnv() === ENV.STAGING;
}

/**
 * Check if the current environment is development.
 */
export function isDevelopment(): boolean {
  return getEnv() === ENV.DEVELOPMENT;
}

/**
 * Check if the current environment is test.
 */
export function isTest(): boolean {
  return getEnv() === ENV.TEST;
}

/**
 * Check if the current environment matches any of the provided environments.
 * Performs case-insensitive comparison.
 *
 * @example
 * isEnv('production') // true if production
 * isEnv('staging', 'development') // true if staging OR development
 */
export function isEnv(...environments: string[]): boolean {
  const currentEnv = getEnv();
  return environments.some((env) => normalizeEnv(env) === currentEnv);
}
